import { createSlice } from '@reduxjs/toolkit'
import name from './name'

/*
 * Release notes ("announcements" on the Ark, "What's new" in the UI —
 * PENG-3809). One list, reverse-chronological, paged with `{ from, size }`,
 * plus the note currently open on the detail page. The server does the
 * tenant/audience filtering (PENG-3662); the client only pages.
 */
/** `currentError` when the note does not exist (or is no longer published). */
export const NOT_FOUND_ERROR = 'not-found'

/**
 * The bus hands `onError` whatever the server sent, and the shape is not
 * agreed yet (TODO(API) in event-bus.js). Only what is unmistakably "no such
 * note" counts; anything else is a failed request the user can retry.
 */
const isNotFound = error =>
  error === NOT_FOUND_ERROR || error?.statusCode === 404 || error?.status === 404

export const initialState = {
  items: [],
  total: 0,
  loading: false,
  loadingMore: false,
  error: null,
  hasLoaded: false,
  current: null,
  currentLoading: false,
  currentError: null
}

const announcements = createSlice({
  name,
  initialState,
  reducers: {
    setLoading: state => {
      state.loading = true
      state.error = null
    },
    setLoadingMore: state => {
      state.loadingMore = true
      state.error = null
    },
    /**
     * The paginated envelope `{ items, total }`. Whether it extends or
     * replaces the list is decided by the request that was made —
     * `setLoadingMore` marks a "Load more" — not by a `from` echoed back, so
     * the client does not depend on the server returning it.
     */
    setAnnouncements: (state, { payload }) => {
      const { items = [], total } = payload || {}
      state.items = state.loadingMore ? [...state.items, ...items] : items
      state.total = typeof total === 'number' ? total : state.items.length
      state.loading = false
      state.loadingMore = false
      state.hasLoaded = true
      state.error = null
    },
    setError: (state, { payload }) => {
      state.loading = false
      state.loadingMore = false
      state.error = payload || 'error'
    },
    setCurrentLoading: (state, { payload }) => {
      // Keep the open note when the same one is asked for again, so a
      // re-render doesn't flash the skeleton over content already on screen.
      if (state.current?.id !== payload) state.current = null
      state.currentLoading = true
      state.currentError = null
    },
    setCurrent: (state, { payload }) => {
      state.current = payload
      state.currentLoading = false
      state.currentError = null
    },
    setCurrentError: (state, { payload }) => {
      state.current = null
      state.currentLoading = false
      state.currentError = isNotFound(payload) ? NOT_FOUND_ERROR : 'error'
    },
    clearCurrent: state => {
      state.current = null
      state.currentLoading = false
      state.currentError = null
    }
  }
})

export const {
  setLoading,
  setLoadingMore,
  setAnnouncements,
  setError,
  setCurrentLoading,
  setCurrent,
  setCurrentError,
  clearCurrent
} = announcements.actions

export default announcements.reducer
